import { ApiService } from '../config/apiService';
import store from '../redux/store';
import { logout, updateAccessToken } from '../redux/slices/authSlice';
import { removeItem } from '../utility/localStorageControl';

class SessionValidationService {
  constructor() {
    this.isValidating = false;
    this.validationPromise = null;
    this.lastValidationTime = 0;
    this.validationInterval = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Validate the current session and token
   * @returns {Promise<boolean>} - true if session is valid, false otherwise
   */
  async validateSession() {
    // Prevent multiple simultaneous validations
    if (this.isValidating) {
      return this.validationPromise;
    }

    // Check if we need to validate (avoid too frequent validations)
    const now = Date.now();
    if (now - this.lastValidationTime < this.validationInterval) {
      return true; // Recently validated, assume valid
    }

    this.isValidating = true;
    this.validationPromise = this._performValidation();
    
    try {
      const result = await this.validationPromise;
      this.lastValidationTime = now;
      return result;
    } finally {
      this.isValidating = false;
      this.validationPromise = null;
    }
  }

  /**
   * Perform the actual session validation
   * @private
   */
  async _performValidation() {
    try {
      const state = store.getState();
      const accessToken = state?.auth?.user?.access_token;
      const refreshToken = state?.auth?.user?.refresh_token;

      // No tokens, session is invalid
      if (!accessToken || !refreshToken) {
        console.log('🔒 No tokens found, session invalid');
        return false;
      }

      // Validate token with backend (if endpoint exists)
      try {
        const response = await ApiService.get('/validate-session');
        
        if (response.status === 200) {
          console.log('✅ Session validation successful');
          return true;
        }
      } catch (error) {
        // If validate-session endpoint doesn't exist, fall back to token format validation
        console.log('⚠️ validate-session endpoint not found, using token format validation');
        return this._validateTokenFormat();
      }

      return false;
    } catch (error) {
      console.log('❌ Session validation failed:', error);
      
      // Handle specific error cases
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 401: // Unauthorized
            console.log('🔒 Token expired, attempting refresh...');
            return await this._refreshToken();
            
          case 422: // Unprocessable Entity (invalid session)
            console.log('🔒 Invalid session (422), logging out...');
            await this._handleInvalidSession();
            return false;
            
          case 403: // Forbidden
            console.log('🔒 Access forbidden, logging out...');
            await this._handleInvalidSession();
            return false;
            
          default:
            console.log(`🔒 Session validation error: ${status}`);
            return false;
        }
      }
      
      return false;
    }
  }

  /**
   * Attempt to refresh the access token
   * @private
   */
  async _refreshToken() {
    try {
      const state = store.getState();
      const refreshToken = state?.auth?.user?.refresh_token;

      if (!refreshToken) {
        console.log('🔒 No refresh token available');
        await this._handleInvalidSession();
        return false;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Token refreshed successfully');
        
        // Update the access token in store
        store.dispatch(updateAccessToken(data.access_token));
        
        // Update user object if it exists
        const currentUser = store.getState()?.auth?.user;
        if (currentUser) {
          store.dispatch(updateAccessToken(data.access_token));
        }
        
        return true;
      } else {
        console.log('🔒 Token refresh failed');
        await this._handleInvalidSession();
        return false;
      }
    } catch (error) {
      console.log('❌ Token refresh error:', error);
      await this._handleInvalidSession();
      return false;
    }
  }

  /**
   * Handle invalid session by logging out and clearing data
   * @private
   */
  async _handleInvalidSession() {
    console.log('🔒 Handling invalid session...');
    
    // Clear local storage
    removeItem('userLoc');
    removeItem('userAddress');
    
    // Logout from store
    await store.dispatch(logout());
    
    // Redirect to login (if we have access to navigation)
    if (window.location.pathname !== '/user-login' && window.location.pathname !== '/chef-login') {
      window.location.href = '/user-login';
    }
  }

  /**
   * Check if current session is valid (synchronous check)
   * @returns {boolean} - true if session appears valid
   */
  isSessionValid() {
    const state = store.getState();
    const accessToken = state?.auth?.user?.access_token;
    const refreshToken = state?.auth?.user?.refresh_token;
    
    return !!(accessToken && refreshToken);
  }

  /**
   * Force session validation (ignores cache)
   * @returns {Promise<boolean>} - validation result
   */
  async forceValidateSession() {
    this.lastValidationTime = 0; // Reset cache
    return await this.validateSession();
  }

  /**
   * Setup periodic session validation
   */
  startPeriodicValidation() {
    // Validate every 10 minutes
    setInterval(async () => {
      if (this.isSessionValid()) {
        await this.validateSession();
      }
    }, 10 * 60 * 1000);
  }

  /**
   * Validate token format and basic structure
   * @private
   */
  _validateTokenFormat() {
    try {
      const state = store.getState();
      const accessToken = state?.auth?.user?.access_token;
      
      if (!accessToken) {
        return false;
      }

      // Basic JWT token format validation
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        console.log('🔒 Invalid token format');
        return false;
      }

      // Check if token is not expired (basic check)
      try {
        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        if (payload.exp && payload.exp < currentTime) {
          console.log('🔒 Token expired');
          return false;
        }
        
        console.log('✅ Token format validation passed');
        return true;
      } catch (parseError) {
        console.log('🔒 Token payload parsing failed');
        return false;
      }
    } catch (error) {
      console.log('❌ Token format validation error:', error);
      return false;
    }
  }

  /**
   * Handle 422 errors from API calls
   * @param {Error} error - The API error
   * @returns {Promise<boolean>} - true if handled successfully
   */
  async handle422Error(error) {
    if (error.response && error.response.status === 422) {
      console.log('🔒 422 error detected, validating session...');
      
      // Force session validation
      const isValid = await this.forceValidateSession();
      
      if (!isValid) {
        console.log('🔒 Session invalid after 422, logging out...');
        await this._handleInvalidSession();
        return false;
      }
      
      return true;
    }
    
    return false;
  }
}

// Create singleton instance
const sessionValidationService = new SessionValidationService();

export default sessionValidationService;
